import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { profileVerificationSumsubTokenQueryKey } from '@/hooks/queries/use-profile-verification-information-sumsub-token';
import { getNationalClientIdentifier, postNationalClientIdentifier } from '@/services/api/profile/national-client-identifier';

const profileNationalClientIdentifierQueryKey = (pid: UserPid) => ['profile', 'national-client-identifier', pid];

const profileNationalClientIdentifierQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getNationalClientIdentifier,
    queryKey: profileNationalClientIdentifierQueryKey(pid),
  });

const useProfileNationalClientIdentifier = (enabled = true) => {
  const { pid } = useUserPid();
  const { enabled: queryOptionsEnabled, ...queryOptions } = profileNationalClientIdentifierQueryOptions(pid);

  return useQuery({
    ...queryOptions,
    enabled: queryOptionsEnabled && enabled,
  });
};

const useProfileNationalClientIdentifierMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postNationalClientIdentifier,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: profileNationalClientIdentifierQueryKey(pid) }),
        queryClient.invalidateQueries({ queryKey: profileVerificationSumsubTokenQueryKey(pid) }),
      ]);
    },
  });
};

export { useProfileNationalClientIdentifier, useProfileNationalClientIdentifierMutation };
